import { authenticatedFetch } from "./apiClient";
import { ApiRequestError, parseJsonOrThrow } from "../utils/apiRequestError";
import {
  normalizePolicyEntryActions,
  normalizePolicyExitLegs,
  normalizePolicyKinds,
} from "../utils/policyKindChips";
import type {
  WorkspaceAccountScope,
} from "../types/workspaceAccountScope";
import type {
  FederatedWorkspaceDetail,
  StrategyWorkspaceContribution,
  StrategyWorkspacesResponse,
  WorkspaceDetail,
  WorkspaceEdge,
  WorkspaceNode,
  WorkspaceNodeMeta,
  WorkspaceNodePosition,
  WorkspaceNodeStatus,
  WorkspaceNodeType,
  WorkspaceSummary,
  WorkspaceViewport,
} from "../types/workspace";

export class WorkspacesApiError extends Error {
  status: number;
  slug?: string;

  constructor(message: string, status: number, slug?: string) {
    super(message);
    this.name = "WorkspacesApiError";
    this.status = status;
    this.slug = slug;
  }
}

function accountQuery(scope: WorkspaceAccountScope): string {
  const params = new URLSearchParams({
    broker_name: scope.brokerName.trim(),
    broker_account_code: scope.brokerAccountCode.trim(),
  });
  return params.toString();
}

function scopedPath(path: string, scope: WorkspaceAccountScope): string {
  return `${path}?${accountQuery(scope)}`;
}

async function parseWorkspaceJsonOrThrow<T>(
  response: Response,
  fallback: string,
  options?: { allow204?: boolean }
): Promise<T> {
  try {
    const data = await parseJsonOrThrow<{ data?: T } | T>(response, fallback, {
      allow204: options?.allow204,
    });
    if (response.status === 204) return undefined as T;
    return (data && typeof data === "object" && "data" in data ? data.data : data) as T;
  } catch (err) {
    const slug =
      err instanceof ApiRequestError
        ? err.slug
        : (err as { parsed?: { slug?: string } })?.parsed?.slug;
    const message = err instanceof Error ? err.message : fallback;
    const status = err instanceof ApiRequestError ? err.status : (err as { status?: number })?.status ?? response.status;
    throw new WorkspacesApiError(message, status, slug);
  }
}

function readRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" ? (value as Record<string, unknown>) : null;
}

function readNumber(value: unknown, fallback = 0): number {
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function normalizePosition(raw: unknown): WorkspaceNodePosition {
  let value = raw;
  if (typeof value === "string") {
    try {
      value = JSON.parse(value) as unknown;
    } catch {
      value = null;
    }
  }
  const rec = readRecord(value);
  return {
    x: readNumber(rec?.x),
    y: readNumber(rec?.y),
  };
}

function normalizeViewport(raw: unknown): WorkspaceViewport {
  const rec = readRecord(raw);
  return {
    x: readNumber(rec?.x),
    y: readNumber(rec?.y),
    zoom: readNumber(rec?.zoom, 1) || 1,
  };
}

const WORKSPACE_NODE_TYPES = new Set<WorkspaceNodeType>([
  "account",
  "strategy",
  "report",
  "simulation",
  "policy",
  "combined_policy",
  "combined_simulation",
]);

function normalizeNodeType(raw: unknown): WorkspaceNodeType {
  const value = String(raw ?? "").trim().toLowerCase();
  if (WORKSPACE_NODE_TYPES.has(value as WorkspaceNodeType)) {
    return value as WorkspaceNodeType;
  }
  return "account";
}

function readOptionalNumber(value: unknown): number | null | undefined {
  if (value === undefined) return undefined;
  if (value === null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function normalizeMetaAccounts(raw: unknown): WorkspaceNodeMeta["accounts"] {
  if (!Array.isArray(raw)) return undefined;
  const accounts: NonNullable<WorkspaceNodeMeta["accounts"]> = [];
  for (const item of raw) {
    const rec = readRecord(item);
    if (!rec) continue;
    const brokerName = String(rec.brokerName ?? rec.broker_name ?? "")
      .trim()
      .toLowerCase();
    const brokerAccountCode = String(
      rec.brokerAccountCode ?? rec.broker_account_code ?? ""
    ).trim();
    if (!brokerName || !brokerAccountCode) continue;
    accounts.push({ brokerName, brokerAccountCode });
  }
  return accounts.length > 0 ? accounts : undefined;
}

function readOptionalBoolean(value: unknown): boolean | undefined {
  if (value === true) return true;
  if (value === false) return false;
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();
  if (normalized === "true" || normalized === "1") return true;
  if (normalized === "false" || normalized === "0") return false;
  return undefined;
}

function normalizeMetaStringList(raw: unknown): string[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out: string[] = [];
  for (const item of raw) {
    const value = String(item ?? "").trim();
    if (!value) continue;
    out.push(value);
  }
  return out.length > 0 ? out : undefined;
}

function normalizeMetaSourcePolicies(
  raw: unknown
): WorkspaceNodeMeta["sourcePolicies"] {
  if (!Array.isArray(raw)) return undefined;
  const rows: NonNullable<WorkspaceNodeMeta["sourcePolicies"]> = [];
  for (const item of raw) {
    const rec = readRecord(item);
    if (!rec) continue;
    const policyVersionId = String(
      rec.policyVersionId ?? rec.policy_version_id ?? ""
    ).trim();
    if (!policyVersionId) continue;
    const versionNumber = readOptionalNumber(
      rec.versionNumber ?? rec.version_number
    );
    const titleRaw = rec.title;
    const title =
      titleRaw != null && String(titleRaw).trim()
        ? String(titleRaw).trim()
        : null;
    const brokerNameRaw = rec.brokerName ?? rec.broker_name;
    const brokerName =
      brokerNameRaw != null && String(brokerNameRaw).trim()
        ? String(brokerNameRaw).trim().toLowerCase()
        : null;
    const brokerAccountCodeRaw =
      rec.brokerAccountCode ?? rec.broker_account_code;
    const brokerAccountCode =
      brokerAccountCodeRaw != null && String(brokerAccountCodeRaw).trim()
        ? String(brokerAccountCodeRaw).trim()
        : null;
    const onThisCanvas = readOptionalBoolean(rec.onThisCanvas ?? rec.on_this_canvas);
    rows.push({
      policyVersionId,
      ...(versionNumber !== undefined ? { versionNumber } : {}),
      ...(title != null ? { title } : {}),
      ...(brokerName ? { brokerName } : {}),
      ...(brokerAccountCode ? { brokerAccountCode } : {}),
      onThisCanvas: onThisCanvas !== false,
    });
  }
  return rows.length > 0 ? rows : undefined;
}

function normalizeMeta(raw: unknown): WorkspaceNodeMeta | undefined {
  const rec = readRecord(raw);
  if (!rec) return undefined;
  const title = String(rec.title ?? rec.name ?? "").trim();
  const status = rec.status as WorkspaceNodeStatus | undefined;
  // Strategy cards use subtitle for description; BE may stamp either key.
  const subtitleRaw = rec.subtitle ?? rec.description;
  const subtitle = subtitleRaw != null ? String(subtitleRaw) : undefined;
  const adoptedReportId =
    rec.adoptedReportId != null || rec.adopted_report_id != null
      ? String(rec.adoptedReportId ?? rec.adopted_report_id).trim() || undefined
      : undefined;
  const sourceRaw = rec.sourcePolicyVersionIds ?? rec.source_policy_version_ids;
  const sourcePolicyVersionIds = Array.isArray(sourceRaw)
    ? sourceRaw.map((id) => String(id).trim()).filter(Boolean)
    : undefined;
  const serverNodeId =
    rec.serverNodeId != null || rec.server_node_id != null
      ? String(rec.serverNodeId ?? rec.server_node_id).trim() || undefined
      : undefined;
  const isRerun = readOptionalBoolean(rec.isRerun ?? rec.is_rerun) === true;
  const rerunOfEntityId =
    rec.rerunOfEntityId != null || rec.rerun_of_entity_id != null
      ? String(rec.rerunOfEntityId ?? rec.rerun_of_entity_id).trim() || undefined
      : undefined;
  const isFork = readOptionalBoolean(rec.isFork ?? rec.is_fork) === true;
  const forkedFromEntityId =
    rec.forkedFromEntityId != null || rec.forked_from_entity_id != null
      ? String(rec.forkedFromEntityId ?? rec.forked_from_entity_id).trim() ||
        undefined
      : undefined;
  const reportTypeRaw = rec.reportType ?? rec.report_type;
  const reportType =
    reportTypeRaw != null && String(reportTypeRaw).trim()
      ? String(reportTypeRaw).trim()
      : undefined;
  const reportTypeLabelRaw = rec.reportTypeLabel ?? rec.report_type_label;
  const reportTypeLabel =
    reportTypeLabelRaw != null && String(reportTypeLabelRaw).trim()
      ? String(reportTypeLabelRaw).trim()
      : undefined;
  const createdAtRaw = rec.createdAt ?? rec.created_at;
  const createdAt =
    createdAtRaw != null && String(createdAtRaw).trim()
      ? String(createdAtRaw).trim()
      : undefined;
  const updatedAtRaw = rec.updatedAt ?? rec.updated_at;
  const updatedAt =
    updatedAtRaw != null && String(updatedAtRaw).trim()
      ? String(updatedAtRaw).trim()
      : undefined;
  const hasMasterPine = readOptionalBoolean(rec.hasMasterPine ?? rec.has_master_pine);
  const hasActivePine = readOptionalBoolean(rec.hasActivePine ?? rec.has_active_pine);
  const policyVersionCountRaw = readOptionalNumber(
    rec.policyVersionCount ?? rec.policy_version_count
  );
  const policyVersionCount =
    policyVersionCountRaw != null
      ? Math.max(0, Math.trunc(policyVersionCountRaw))
      : undefined;
  const pnl = readOptionalNumber(rec.pnl);
  const winRate = readOptionalNumber(rec.winRate ?? rec.win_rate);
  const tradeCount = readOptionalNumber(rec.tradeCount ?? rec.trade_count);
  const tradesChanged = readOptionalNumber(
    rec.tradesChanged ?? rec.trades_changed
  );
  const pnlChange = readOptionalNumber(rec.pnlChange ?? rec.pnl_change);
  const winRateDelta = readOptionalNumber(rec.winRateDelta ?? rec.win_rate_delta);
  const accounts = normalizeMetaAccounts(rec.accounts);
  const versionNumber = readOptionalNumber(
    rec.versionNumber ?? rec.version_number
  );
  const isCombined = readOptionalBoolean(rec.isCombined ?? rec.is_combined);
  const isCrossAccountCombined = readOptionalBoolean(
    rec.isCrossAccountCombined ?? rec.is_cross_account_combined
  );
  const sourcePolicies = normalizeMetaSourcePolicies(
    rec.sourcePolicies ?? rec.source_policies
  );
  const candleTimeframeRaw =
    rec.candleTimeframe ??
    rec.candle_timeframe ??
    rec.resolvedCandleTimeframe ??
    rec.resolved_candle_timeframe;
  const candleTimeframe =
    candleTimeframeRaw != null && String(candleTimeframeRaw).trim()
      ? String(candleTimeframeRaw).trim()
      : undefined;
  const entryCandleTimeframeRaw =
    rec.entryCandleTimeframe ?? rec.entry_candle_timeframe;
  const entryCandleTimeframe =
    entryCandleTimeframeRaw != null && String(entryCandleTimeframeRaw).trim()
      ? String(entryCandleTimeframeRaw).trim()
      : undefined;
  const managementCandleTimeframeRaw =
    rec.managementCandleTimeframe ?? rec.management_candle_timeframe;
  const managementCandleTimeframe =
    managementCandleTimeframeRaw != null &&
    String(managementCandleTimeframeRaw).trim()
      ? String(managementCandleTimeframeRaw).trim()
      : undefined;
  const resolvedCandleTimeframeRaw =
    rec.resolvedCandleTimeframe ?? rec.resolved_candle_timeframe;
  const resolvedCandleTimeframe =
    resolvedCandleTimeframeRaw != null &&
    String(resolvedCandleTimeframeRaw).trim()
      ? String(resolvedCandleTimeframeRaw).trim()
      : undefined;
  const candleTimeframes = normalizeMetaStringList(
    rec.candleTimeframes ?? rec.candle_timeframes
  );
  const policyKinds = normalizePolicyKinds(rec.policyKinds ?? rec.policy_kinds);
  const entryActions = normalizePolicyEntryActions(
    rec.entryActions ?? rec.entry_actions
  );
  const exitLegs = normalizePolicyExitLegs(rec.exitLegs ?? rec.exit_legs) ?? undefined;
  const windowLabelRaw = rec.windowLabel ?? rec.window_label;
  const windowLabel =
    windowLabelRaw != null && String(windowLabelRaw).trim()
      ? String(windowLabelRaw).trim()
      : undefined;
  /** Cohort window preset — not candle TF (`candleTimeframe`). */
  const cohortTimeframeRaw = rec.timeframe;
  const timeframe =
    cohortTimeframeRaw != null && String(cohortTimeframeRaw).trim()
      ? String(cohortTimeframeRaw).trim()
      : undefined;
  const localStartDateRaw = rec.localStartDate ?? rec.local_start_date;
  const localStartDate =
    localStartDateRaw != null && String(localStartDateRaw).trim()
      ? String(localStartDateRaw).trim()
      : undefined;
  const localEndDateRaw = rec.localEndDate ?? rec.local_end_date;
  const localEndDate =
    localEndDateRaw != null && String(localEndDateRaw).trim()
      ? String(localEndDateRaw).trim()
      : undefined;

  const hasCardStats =
    pnl !== undefined ||
    winRate !== undefined ||
    tradeCount !== undefined ||
    tradesChanged !== undefined ||
    pnlChange !== undefined ||
    winRateDelta !== undefined ||
    Boolean(accounts?.length) ||
    versionNumber !== undefined ||
    isCombined !== undefined ||
    isCrossAccountCombined !== undefined ||
    Boolean(sourcePolicies?.length) ||
    Boolean(reportType) ||
    Boolean(reportTypeLabel) ||
    Boolean(createdAt) ||
    Boolean(updatedAt) ||
    hasMasterPine !== undefined ||
    hasActivePine !== undefined ||
    policyVersionCount !== undefined ||
    Boolean(candleTimeframe) ||
    Boolean(candleTimeframes?.length) ||
    Boolean(entryCandleTimeframe) ||
    Boolean(managementCandleTimeframe) ||
    Boolean(resolvedCandleTimeframe) ||
    Boolean(windowLabel) ||
    Boolean(timeframe) ||
    Boolean(localStartDate) ||
    Boolean(localEndDate) ||
    policyKinds.length > 0 ||
    entryActions.length > 0 ||
    Boolean(exitLegs);

  if (
    !title &&
    !status &&
    !subtitle &&
    !adoptedReportId &&
    !(sourcePolicyVersionIds && sourcePolicyVersionIds.length > 0) &&
    !serverNodeId &&
    !isRerun &&
    !rerunOfEntityId &&
    !isFork &&
    !forkedFromEntityId &&
    !hasCardStats
  ) {
    return undefined;
  }
  return {
    title: title || "Untitled",
    status,
    subtitle,
    ...(adoptedReportId ? { adoptedReportId } : {}),
    ...(sourcePolicyVersionIds && sourcePolicyVersionIds.length > 0
      ? { sourcePolicyVersionIds }
      : {}),
    ...(serverNodeId ? { serverNodeId } : {}),
    ...(isRerun ? { isRerun: true } : {}),
    ...(rerunOfEntityId ? { rerunOfEntityId } : {}),
    ...(isFork ? { isFork: true } : {}),
    ...(forkedFromEntityId ? { forkedFromEntityId } : {}),
    ...(reportType ? { reportType } : {}),
    ...(reportTypeLabel ? { reportTypeLabel } : {}),
    ...(createdAt ? { createdAt } : {}),
    ...(updatedAt ? { updatedAt } : {}),
    ...(hasMasterPine !== undefined ? { hasMasterPine } : {}),
    ...(hasActivePine !== undefined ? { hasActivePine } : {}),
    ...(policyVersionCount !== undefined ? { policyVersionCount } : {}),
    ...(pnl !== undefined ? { pnl } : {}),
    ...(winRate !== undefined ? { winRate } : {}),
    ...(tradeCount !== undefined ? { tradeCount } : {}),
    ...(tradesChanged !== undefined ? { tradesChanged } : {}),
    ...(pnlChange !== undefined ? { pnlChange } : {}),
    ...(winRateDelta !== undefined ? { winRateDelta } : {}),
    ...(accounts ? { accounts } : {}),
    ...(versionNumber !== undefined ? { versionNumber } : {}),
    ...(isCombined !== undefined ? { isCombined } : {}),
    ...(isCrossAccountCombined !== undefined ? { isCrossAccountCombined } : {}),
    ...(sourcePolicies ? { sourcePolicies } : {}),
    ...(policyKinds.length > 0 ? { policyKinds } : {}),
    ...(entryActions.length > 0 ? { entryActions } : {}),
    ...(exitLegs ? { exitLegs } : {}),
    ...(candleTimeframe ? { candleTimeframe } : {}),
    ...(candleTimeframes ? { candleTimeframes } : {}),
    ...(entryCandleTimeframe ? { entryCandleTimeframe } : {}),
    ...(managementCandleTimeframe ? { managementCandleTimeframe } : {}),
    ...(resolvedCandleTimeframe ? { resolvedCandleTimeframe } : {}),
    ...(windowLabel ? { windowLabel } : {}),
    ...(timeframe ? { timeframe } : {}),
    ...(localStartDate ? { localStartDate } : {}),
    ...(localEndDate ? { localEndDate } : {}),
  };
}

function normalizeEdge(raw: unknown): WorkspaceEdge {
  const rec = readRecord(raw) ?? {};
  return {
    id: String(rec.id),
    sourceNodeId: String(rec.sourceNodeId ?? rec.source_node_id ?? ""),
    targetNodeId: String(rec.targetNodeId ?? rec.target_node_id ?? ""),
  };
}

function normalizeSummary(raw: unknown): WorkspaceSummary {
  const rec = readRecord(raw) ?? {};
  return {
    id: String(rec.id),
    name: String(rec.name ?? ""),
    brokerName: String(rec.brokerName ?? rec.broker_name ?? ""),
    brokerAccountCode: String(rec.brokerAccountCode ?? rec.broker_account_code ?? ""),
    updatedAt: String(rec.updatedAt ?? rec.updated_at ?? ""),
    createdAt:
      rec.createdAt != null || rec.created_at != null
        ? String(rec.createdAt ?? rec.created_at)
        : undefined,
    nodeCount:
      rec.nodeCount != null || rec.node_count != null
        ? readNumber(rec.nodeCount ?? rec.node_count)
        : undefined,
  };
}

function normalizeNode(raw: unknown): WorkspaceNode | null {
  const rec = readRecord(raw) ?? {};
  const id = String(rec.id ?? rec.nodeId ?? rec.node_id ?? "").trim();
  if (!id || id === "undefined" || id === "null") return null;
  const sourceWorkspaceIdRaw = rec.sourceWorkspaceId ?? rec.source_workspace_id;
  const sourceNodeIdRaw = rec.sourceNodeId ?? rec.source_node_id;
  const brokerNameRaw = rec.brokerName ?? rec.broker_name;
  const brokerAccountCodeRaw = rec.brokerAccountCode ?? rec.broker_account_code;
  const sourceWorkspaceId =
    sourceWorkspaceIdRaw != null && String(sourceWorkspaceIdRaw).trim()
      ? String(sourceWorkspaceIdRaw).trim()
      : null;
  const sourceNodeId =
    sourceNodeIdRaw != null && String(sourceNodeIdRaw).trim()
      ? String(sourceNodeIdRaw).trim()
      : null;
  const brokerName =
    brokerNameRaw != null && String(brokerNameRaw).trim()
      ? String(brokerNameRaw).trim()
      : null;
  const brokerAccountCode =
    brokerAccountCodeRaw != null && String(brokerAccountCodeRaw).trim()
      ? String(brokerAccountCodeRaw).trim()
      : null;
  return {
    id,
    type: normalizeNodeType(rec.type),
    entityId: String(rec.entityId ?? rec.entity_id ?? ""),
    position: normalizePosition(rec.position ?? rec.position_json),
    isDisabled: Boolean(rec.isDisabled ?? rec.is_disabled),
    meta: normalizeMeta(rec.meta ?? rec.meta_json),
    ...(sourceWorkspaceId != null ? { sourceWorkspaceId } : {}),
    ...(sourceNodeId != null ? { sourceNodeId } : {}),
    ...(brokerName != null ? { brokerName } : {}),
    ...(brokerAccountCode != null ? { brokerAccountCode } : {}),
  };
}

function collectGraphSources(rec: Record<string, unknown>): Record<string, unknown>[] {
  const sources: Record<string, unknown>[] = [rec];
  const nested = readRecord(rec.workspace);
  if (nested) sources.push(nested);
  const graph = readRecord(rec.graph);
  if (graph) sources.push(graph);
  return sources;
}

function readArrayFromSources(
  sources: Record<string, unknown>[],
  keys: string[]
): unknown[] {
  for (const src of sources) {
    for (const key of keys) {
      const value = src[key];
      if (Array.isArray(value) && value.length > 0) return value;
    }
  }
  for (const src of sources) {
    for (const key of keys) {
      const value = src[key];
      if (Array.isArray(value)) return value;
    }
  }
  return [];
}

function normalizeDetail(raw: unknown): WorkspaceDetail {
  const rec = readRecord(raw) ?? {};
  const nested = readRecord(rec.workspace);
  const base = nested ?? rec;
  const graphSources = collectGraphSources(rec);
  const nodesRaw = readArrayFromSources(graphSources, ["nodes", "workspace_nodes", "workspaceNodes"]);
  const edgesRaw = readArrayFromSources(graphSources, [
    "edges",
    "workspace_edges",
    "workspaceEdges",
  ]);
  const nodes = nodesRaw
    .map(normalizeNode)
    .filter((node): node is WorkspaceNode => node != null);
  return {
    id: String(base.id ?? rec.id ?? ""),
    name: String(base.name ?? rec.name ?? ""),
    brokerName: String(base.brokerName ?? base.broker_name ?? rec.brokerName ?? rec.broker_name ?? ""),
    brokerAccountCode: String(
      base.brokerAccountCode ??
        base.broker_account_code ??
        rec.brokerAccountCode ??
        rec.broker_account_code ??
        ""
    ),
    viewport: normalizeViewport(
      base.viewport ?? base.viewport_json ?? rec.viewport ?? rec.viewport_json
    ),
    nodes,
    edges: edgesRaw.map(normalizeEdge).filter((edge) => edge.id && edge.sourceNodeId && edge.targetNodeId),
    createdAt:
      base.createdAt != null ||
      base.created_at != null ||
      rec.createdAt != null ||
      rec.created_at != null
        ? String(base.createdAt ?? base.created_at ?? rec.createdAt ?? rec.created_at)
        : undefined,
    updatedAt:
      base.updatedAt != null ||
      base.updated_at != null ||
      rec.updatedAt != null ||
      rec.updated_at != null
        ? String(base.updatedAt ?? base.updated_at ?? rec.updatedAt ?? rec.updated_at)
        : undefined,
  };
}

/** @internal Exposed for unit tests. */
export function normalizeWorkspaceDetailResponse(raw: unknown): WorkspaceDetail {
  return normalizeDetail(raw);
}

function normalizeStrategyContribution(raw: unknown): StrategyWorkspaceContribution | null {
  const rec = readRecord(raw) ?? {};
  const workspaceId = String(rec.workspaceId ?? rec.workspace_id ?? "").trim();
  if (!workspaceId) return null;
  const strategyNodeId = String(rec.strategyNodeId ?? rec.strategy_node_id ?? "").trim();
  return {
    workspaceId,
    name: rec.name != null ? String(rec.name) : null,
    brokerName:
      rec.brokerName != null || rec.broker_name != null
        ? String(rec.brokerName ?? rec.broker_name)
        : null,
    brokerAccountCode:
      rec.brokerAccountCode != null || rec.broker_account_code != null
        ? String(rec.brokerAccountCode ?? rec.broker_account_code)
        : null,
    ...(strategyNodeId ? { strategyNodeId } : {}),
    createdAt:
      rec.createdAt != null || rec.created_at != null
        ? String(rec.createdAt ?? rec.created_at)
        : null,
    updatedAt:
      rec.updatedAt != null || rec.updated_at != null
        ? String(rec.updatedAt ?? rec.updated_at)
        : null,
  };
}

/** @internal Exposed for unit tests. */
export function normalizeStrategyWorkspacesResponse(raw: unknown): StrategyWorkspacesResponse {
  const rec = readRecord(raw) ?? {};
  const list = Array.isArray(rec.workspaces) ? rec.workspaces : [];
  return {
    strategyId: String(rec.strategyId ?? rec.strategy_id ?? "").trim(),
    workspaces: list
      .map(normalizeStrategyContribution)
      .filter((row): row is StrategyWorkspaceContribution => row != null),
  };
}

/**
 * Map federated Strategy canvas into WorkspaceDetail for shared canvas chrome.
 * Synthetic id `strategy:{strategyId}`; broker fields from first contributing workspace.
 */
/** @internal Exposed for unit tests. */
export function normalizeFederatedWorkspaceResponse(raw: unknown): FederatedWorkspaceDetail {
  const rec = readRecord(raw) ?? {};
  const strategyId = String(rec.strategyId ?? rec.strategy_id ?? "").trim();
  const contributingRaw = Array.isArray(rec.contributingWorkspaces)
    ? rec.contributingWorkspaces
    : Array.isArray(rec.contributing_workspaces)
      ? rec.contributing_workspaces
      : [];
  const contributingWorkspaces = contributingRaw
    .map(normalizeStrategyContribution)
    .filter((row): row is StrategyWorkspaceContribution => row != null);
  const nodesRaw = Array.isArray(rec.nodes) ? rec.nodes : [];
  const edgesRaw = Array.isArray(rec.edges) ? rec.edges : [];
  return {
    strategyId,
    strategyName:
      rec.strategyName != null || rec.strategy_name != null
        ? String(rec.strategyName ?? rec.strategy_name)
        : null,
    mode: "strategy",
    contributingWorkspaces,
    viewport: { x: 0, y: 0, zoom: 1 },
    nodes: nodesRaw
      .map(normalizeNode)
      .filter((node): node is WorkspaceNode => node != null),
    edges: edgesRaw
      .map(normalizeEdge)
      .filter((edge) => edge.id && edge.sourceNodeId && edge.targetNodeId),
  };
}

export function federatedWorkspaceToDetail(
  fed: FederatedWorkspaceDetail
): WorkspaceDetail {
  const first = fed.contributingWorkspaces[0];
  return {
    id: `strategy:${fed.strategyId}`,
    name: fed.strategyName?.trim() || "Strategy workspace",
    brokerName: first?.brokerName?.trim() || "",
    brokerAccountCode: first?.brokerAccountCode?.trim() || "",
    viewport: { x: 0, y: 0, zoom: 1 },
    nodes: fed.nodes,
    edges: fed.edges,
  };
}

export interface WorkspacesListResponse {
  workspaces: WorkspaceSummary[];
}

export interface CreateWorkspaceRequest {
  name: string;
}

export interface UpdateWorkspaceRequest {
  name?: string;
  viewport?: WorkspaceViewport;
}

export interface RegisterWorkspaceNodeRequest {
  type: WorkspaceNode["type"];
  entityId: string;
  parentNodeId?: string;
  parentNodeIds?: string[];
  position?: WorkspaceNodePosition;
  meta?: WorkspaceNodeMeta;
}

export interface RegisterWorkspaceNodeResponse {
  node: WorkspaceNode;
  edge?: WorkspaceEdge;
  edges?: WorkspaceEdge[];
}

export interface PatchWorkspaceNodeRequest {
  position?: WorkspaceNodePosition;
  isDisabled?: boolean;
}

export interface BulkWorkspaceNodePositionsRequest {
  positions: Array<{ nodeId: string; x: number; y: number }>;
}

function sanitizeBulkPositionEntry(
  entry: { nodeId: string; x: number; y: number }
): { nodeId: string; x: number; y: number } | null {
  const nodeId = String(entry.nodeId ?? "").trim();
  if (!nodeId || nodeId === "undefined" || nodeId === "null") return null;

  const x = Number(entry.x);
  const y = Number(entry.y);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;

  return {
    nodeId,
    x: Math.round(x * 100) / 100,
    y: Math.round(y * 100) / 100,
  };
}

/** @internal Exposed for unit tests. */
export function sanitizeWorkspaceBulkPositions(
  positions: Array<{ nodeId: string; x: number; y: number }>
): Array<{ nodeId: string; x: number; y: number }> {
  return positions
    .map(sanitizeBulkPositionEntry)
    .filter((entry): entry is { nodeId: string; x: number; y: number } => entry != null);
}

export async function listWorkspaces(scope: WorkspaceAccountScope): Promise<WorkspacesListResponse> {
  const response = await authenticatedFetch(scopedPath("/workspaces", scope));
  const data = await parseWorkspaceJsonOrThrow<WorkspacesListResponse | WorkspaceSummary[]>(
    response,
    `Failed to load workspaces: ${response.status}`
  );
  if (Array.isArray(data)) {
    return { workspaces: data.map(normalizeSummary) };
  }
  const workspaces = Array.isArray(data?.workspaces) ? data.workspaces.map(normalizeSummary) : [];
  return { workspaces };
}

export async function createWorkspace(
  scope: WorkspaceAccountScope,
  body: CreateWorkspaceRequest
): Promise<WorkspaceDetail> {
  const response = await authenticatedFetch(scopedPath("/workspaces", scope), {
    method: "POST",
    body: JSON.stringify({
      name: body.name.trim(),
      brokerName: scope.brokerName.trim(),
      brokerAccountCode: scope.brokerAccountCode.trim(),
    }),
  });
  const data = await parseWorkspaceJsonOrThrow<unknown>(
    response,
    `Failed to create workspace: ${response.status}`
  );
  return normalizeDetail(data);
}

export async function getWorkspace(
  scope: WorkspaceAccountScope,
  workspaceId: string
): Promise<WorkspaceDetail> {
  const response = await authenticatedFetch(
    scopedPath(`/workspaces/${encodeURIComponent(workspaceId)}`, scope)
  );
  const data = await parseWorkspaceJsonOrThrow<unknown>(
    response,
    `Failed to load workspace: ${response.status}`
  );
  return normalizeDetail(data);
}

export async function updateWorkspace(
  scope: WorkspaceAccountScope,
  workspaceId: string,
  patch: UpdateWorkspaceRequest
): Promise<WorkspaceDetail> {
  const response = await authenticatedFetch(
    scopedPath(`/workspaces/${encodeURIComponent(workspaceId)}`, scope),
    {
      method: "PATCH",
      body: JSON.stringify(patch),
    }
  );
  const data = await parseWorkspaceJsonOrThrow<unknown>(
    response,
    `Failed to update workspace: ${response.status}`
  );
  return normalizeDetail(data);
}

export async function deleteWorkspace(
  scope: WorkspaceAccountScope,
  workspaceId: string
): Promise<void> {
  const response = await authenticatedFetch(
    scopedPath(`/workspaces/${encodeURIComponent(workspaceId)}`, scope),
    { method: "DELETE" }
  );
  await parseWorkspaceJsonOrThrow(response, `Failed to delete workspace: ${response.status}`, {
    allow204: true,
  });
}

export async function registerWorkspaceNode(
  scope: WorkspaceAccountScope,
  workspaceId: string,
  body: RegisterWorkspaceNodeRequest
): Promise<RegisterWorkspaceNodeResponse> {
  const response = await authenticatedFetch(
    scopedPath(`/workspaces/${encodeURIComponent(workspaceId)}/nodes`, scope),
    {
      method: "POST",
      body: JSON.stringify(body),
    }
  );
  const data = await parseWorkspaceJsonOrThrow<Record<string, unknown>>(
    response,
    `Failed to register workspace node: ${response.status}`
  );
  const node = normalizeNode(data.node);
  if (!node) {
    throw new WorkspacesApiError("Invalid workspace node in response", response.status);
  }
  const edgesRaw = Array.isArray(data.edges) ? data.edges : [];
  const edges = edgesRaw
    .map(normalizeEdge)
    .filter((e) => Boolean(e.sourceNodeId && e.targetNodeId));
  const edge = data.edge != null ? normalizeEdge(data.edge) : edges[0];
  return {
    node,
    ...(edge && edge.sourceNodeId ? { edge } : {}),
    ...(edges.length > 0 ? { edges } : {}),
  };
}

export async function patchWorkspaceNode(
  scope: WorkspaceAccountScope,
  workspaceId: string,
  nodeId: string,
  patch: PatchWorkspaceNodeRequest
): Promise<WorkspaceNode> {
  const response = await authenticatedFetch(
    scopedPath(`/workspaces/${encodeURIComponent(workspaceId)}/nodes/${encodeURIComponent(nodeId)}`, scope),
    {
      method: "PATCH",
      body: JSON.stringify(patch),
    }
  );
  const data = await parseWorkspaceJsonOrThrow<unknown>(
    response,
    `Failed to update workspace node: ${response.status}`
  );
  const node = normalizeNode(data);
  if (!node) {
    throw new WorkspacesApiError("Invalid workspace node in response", response.status);
  }
  return node;
}

async function patchWorkspaceNodePositions(
  scope: WorkspaceAccountScope,
  workspaceId: string,
  positions: Array<{ nodeId: string; x: number; y: number }>
): Promise<void> {
  const patchResults = await Promise.allSettled(
    positions.map((entry) =>
      patchWorkspaceNode(scope, workspaceId, entry.nodeId, {
        position: { x: entry.x, y: entry.y },
      })
    )
  );

  const patchFailures = patchResults.filter(
    (result): result is PromiseRejectedResult => result.status === "rejected"
  );

  if (patchFailures.length === 0) return;

  // Any failure must surface so callers can fall back to bulk-positions instead
  // of silently keeping a half-saved layout.
  throw patchFailures[0]?.reason ?? new WorkspacesApiError("Failed to save node positions", 500);
}

export async function bulkUpdateWorkspaceNodePositions(
  scope: WorkspaceAccountScope,
  workspaceId: string,
  body: BulkWorkspaceNodePositionsRequest
): Promise<void> {
  const positions = sanitizeWorkspaceBulkPositions(body.positions);
  if (positions.length === 0) return;

  // Prefer one bulk request — per-node PATCH storms the network on large canvases.
  const bulkResponse = await authenticatedFetch(
    scopedPath(`/workspaces/${encodeURIComponent(workspaceId)}/nodes/bulk-positions`, scope),
    {
      method: "PATCH",
      body: JSON.stringify({
        positions: positions.map((entry) => ({
          nodeId: entry.nodeId,
          position: { x: entry.x, y: entry.y },
        })),
      }),
    }
  );

  if (bulkResponse.ok) {
    await parseWorkspaceJsonOrThrow(bulkResponse, `Failed to save node positions: ${bulkResponse.status}`, {
      allow204: true,
    });
    return;
  }

  // Older BE builds may lack bulk-positions (or route-order bugs) — fall back to per-node PATCH.
  if (bulkResponse.status === 404 || bulkResponse.status === 405) {
    await patchWorkspaceNodePositions(scope, workspaceId, positions);
    return;
  }

  // Some deployments return 400/501 for unknown bulk shape — still try per-node.
  try {
    await patchWorkspaceNodePositions(scope, workspaceId, positions);
  } catch (patchErr) {
    throw patchErr;
  }
}

export async function reparentWorkspaceNode(
  scope: WorkspaceAccountScope,
  workspaceId: string,
  nodeId: string,
  body: { parentNodeId: string }
): Promise<RegisterWorkspaceNodeResponse> {
  const response = await authenticatedFetch(
    scopedPath(
      `/workspaces/${encodeURIComponent(workspaceId)}/nodes/${encodeURIComponent(nodeId)}/reparent`,
      scope
    ),
    {
      method: "POST",
      body: JSON.stringify({ parentNodeId: body.parentNodeId }),
    }
  );
  const data = await parseWorkspaceJsonOrThrow<Record<string, unknown>>(
    response,
    `Failed to reparent workspace node: ${response.status}`
  );
  const node = normalizeNode(data.node);
  if (!node) {
    throw new WorkspacesApiError("Invalid workspace node in response", response.status);
  }
  return {
    node,
    edge: normalizeEdge(data.edge),
  };
}

export async function breakWorkspaceNodeLink(
  scope: WorkspaceAccountScope,
  workspaceId: string,
  nodeId: string
): Promise<void> {
  const response = await authenticatedFetch(
    scopedPath(
      `/workspaces/${encodeURIComponent(workspaceId)}/nodes/${encodeURIComponent(nodeId)}/break-link`,
      scope
    ),
    { method: "POST" }
  );
  await parseWorkspaceJsonOrThrow(response, `Failed to break workspace link: ${response.status}`);
}
